import React, { memo, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Icons,
  Avatar,
} from '@ublend-npm/aulaui-core';

import { getStarredSpaceColors } from '../../colors';
import SpaceDuration from './SpaceDuration';

export interface Space {
  id: string;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  lastNavigation?: string | null;
  avatar?: string | null;
}

interface SpaceItemProps {
  space: Space;
  isStarred: boolean;
  onStarToggle: (id: string) => void;
  onClick?: (id: string) => void;
}

const SpaceItem = ({
  space,
  isStarred,
  onStarToggle,
  onClick,
}: SpaceItemProps) => {
  const { palette } = useTheme();
  const colors = useMemo(() => getStarredSpaceColors(palette), [palette]);

  const handleStarToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onStarToggle(space.id);
    },
    [onStarToggle, space.id]
  );

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(space.id);
    }
  }, [onClick, space.id]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={1.5}
      borderRadius={2}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
      sx={{
        minWidth: 0,
        width: '100%',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick
          ? {
              bgcolor: 'action.hover',
            }
          : {},
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        gap={2}
        flex={1}
        sx={{ minWidth: 0 }}
      >
        <Avatar
          kind="circular"
          src={space.avatar || undefined}
          fallbackText={space.name?.charAt(0).toUpperCase() || '?'}
          sx={{
            width: 40,
            height: 40,
            fontSize: '1rem',
            flexShrink: 0,
          }}
        />

        <Box flex={1} sx={{ minWidth: 0, pr: 1 }}>
          <Typography
            variant="body1"
            title={space.name}
            color="text.primary"
            sx={{
              fontWeight: 550,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {space.name}
          </Typography>

          <Typography variant="body2" color={colors.textSecondary} noWrap>
            <SpaceDuration
              startDate={space.startDate}
              endDate={space.endDate}
              gap={8}
            />
          </Typography>
        </Box>
      </Box>

      <IconButton
        onClick={handleStarToggle}
        aria-label={`${
          isStarred ? 'Remove from starred spaces' : 'Add to starred spaces'
        }: ${space.name}`}
        aria-pressed={isStarred}
        size="small"
        sx={{
          color: colors.starIcon,
          flexShrink: 0,
        }}
      >
        {isStarred ? (
          <Icons.Star sx={{ width: 20, height: 19 }} />
        ) : (
          <Icons.StarBorder sx={{ width: 20, height: 19 }} />
        )}
      </IconButton>
    </Box>
  );
};

export default memo(SpaceItem);
